/**
 * Create Ticket From Chat - Modal to create a ticket from /createticket slash command.
 * Same fields as the main new ticket form (business, assignee, subject, description, priority, category, type).
 */

import React, { useState } from 'react';
import {
  Modal,
  Select,
  Input,
  Typography,
  message as antMessage,
  Empty,
  Spin,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './CreateTicketFromChatModal.css';

const { TextArea } = Input;
const { Option } = Select;

interface CreateTicketFromChatModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (ticketNumber: string, ticketId: string) => void;
}

export const CreateTicketFromChatModal: React.FC<CreateTicketFromChatModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [form, setForm] = useState({
    assigned_to: user?.id || '',
    subject: '',
    description: '',
    priority: 'medium' as string,
    category_id: '',
    type: '',
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const { data: agentsData } = useQuery(
    'tickets-agents-available',
    () => api.get('/api/tickets/agents/available').then(res => res.data),
    { enabled: open && !!(user?.role === 'agent' || user?.role === 'admin'), staleTime: 5 * 60 * 1000 }
  );
  const agents = agentsData?.agents || [];

  const { data: categoriesData } = useQuery(
    'categories',
    async () => {
      const res = await api.get('/api/categories');
      return Array.isArray(res.data?.categories) ? res.data.categories : [];
    },
    { enabled: open, staleTime: 5 * 60 * 1000 }
  );
  const categories = categoriesData || [];

  const { data: ticketTypesData } = useQuery(
    'ticket-types',
    async () => {
      const res = await api.get('/api/ticket-types');
      return Array.isArray(res.data?.types) ? res.data.types : (res.data?.types ? res.data.types : []);
    },
    { enabled: open, staleTime: 5 * 60 * 1000 }
  );
  const ticketTypes = ticketTypesData || [];

  const { data: searchResults = [], isLoading: isSearching } = useQuery(
    ['businesses-search-chat', businessSearchTerm],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(businessSearchTerm)}&limit=50`)
        .then(res => res.data?.businesses || []),
    { enabled: open && businessSearchTerm.length >= 1 }
  );

  const createMutation = useMutation(
    async (payload: any) => {
      const ticketRes = await api.post('/api/tickets', payload);
      if (payload.description && ticketRes.data?.id) {
        await api.post(`/api/tickets/${ticketRes.data.id}/comments`, {
          comment: payload.description,
          is_internal: false,
        });
      }
      return ticketRes.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['tickets']);
        antMessage.success(`Ticket #${data?.ticket_number || data?.id} created`);
        onSuccess?.(data?.ticket_number, data?.id);
        handleClose();
      },
      onError: (err: any) => {
        antMessage.error(err?.response?.data?.error || 'Failed to create ticket');
      },
    }
  );

  const handleClose = () => {
    setForm({
      assigned_to: user?.id || '',
      subject: '',
      description: '',
      priority: 'medium',
      category_id: '',
      type: '',
    });
    setSelectedBusiness(null);
    setBusinessSearchTerm('');
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!selectedBusiness) newErrors.business = true;
    if (!form.subject?.trim()) newErrors.subject = true;
    if (!form.description?.trim()) newErrors.description = true;
    if (!form.category_id) newErrors.category = true;
    if (!form.type) newErrors.type = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      antMessage.warning('Please fill required fields: Business, Subject, Description, Category, Type');
      return;
    }
    const payload = {
      business_id: (selectedBusiness as any)?.id,
      assigned_to: form.assigned_to || undefined,
      subject: form.subject.trim(),
      description: form.description.trim(),
      priority: form.priority,
      status: 'open',
      category_id: form.category_id || undefined,
      type: form.type,
      created_by: user?.id,
    };
    createMutation.mutate(payload);
  };

  const handleBusinessSelect = (businessId: string) => {
    const business = searchResults.find((b: any) => b.id === businessId);
    if (business) setSelectedBusiness(business);
    else setSelectedBusiness(null);
    setErrors(e => ({ ...e, business: false }));
  };

  const businessOptionsList = selectedBusiness && !searchResults.some((b: any) => b.id === (selectedBusiness as any)?.id)
    ? [selectedBusiness, ...searchResults]
    : searchResults;

  const modalZIndex = 10050;
  const dropdownZIndex = modalZIndex + 10;

  return (
    <Modal
      title="Create ticket (from chat)"
      open={open}
      onCancel={handleClose}
      width={560}
      okText="Create ticket"
      confirmLoading={createMutation.isLoading}
      onOk={handleSubmit}
      destroyOnClose
      maskClosable={!createMutation.isLoading}
      zIndex={modalZIndex}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="create-ticket-from-chat-modal">
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            Business *
          </Typography.Text>
          <Select
            showSearch
            placeholder="Search by name, MID, email..."
            value={selectedBusiness ? (selectedBusiness as any)?.id : undefined}
            filterOption={false}
            onSearch={setBusinessSearchTerm}
            onSelect={handleBusinessSelect}
            onClear={() => {
              setSelectedBusiness(null);
              setBusinessSearchTerm('');
            }}
            allowClear
            style={{ width: '100%' }}
            optionLabelProp="label"
            listHeight={256}
            notFoundContent={
              businessSearchTerm.length < 1 ? (
                <span style={{ color: '#999' }}>Type to search...</span>
              ) : isSearching ? (
                <Spin size="small" />
              ) : (
                <Empty description="No businesses found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
            status={errors.business ? 'error' : undefined}
            dropdownStyle={{ zIndex: dropdownZIndex }}
            popupMatchSelectWidth={false}
            dropdownAlign={{ overflow: { adjustX: true, adjustY: true } }}
          >
            {businessOptionsList.map((b: any) => {
              const address = [b.address, b.city, b.state, b.zip_code].filter(Boolean).join(', ');
              const mid = b.mid || b.merchant_id || 'N/A';
              const subtitle = (address ? `${address} · ` : '') + (mid ? `MID: ${mid}` : '');
              return (
                <Option key={b.id} value={b.id} label={b.business_name}>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.business_name}</div>
                    {subtitle && (
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {subtitle}
                      </Typography.Text>
                    )}
                  </div>
                </Option>
              );
            })}
          </Select>
        </div>

        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            Assignee
          </Typography.Text>
          <Select
            placeholder="Unassigned"
            value={form.assigned_to || undefined}
            onChange={v => setForm(f => ({ ...f, assigned_to: v || '' }))}
            allowClear
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            dropdownStyle={{ zIndex: dropdownZIndex }}
            options={[
              { value: '', label: 'Unassigned' },
              ...agents.map((u: any) => ({
                value: u.id,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
              })),
            ]}
          />
        </div>

        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            Subject *
          </Typography.Text>
          <Input
            placeholder="Ticket subject"
            value={form.subject}
            onChange={e => {
              setForm(f => ({ ...f, subject: e.target.value }));
              setErrors(e2 => ({ ...e2, subject: false }));
            }}
            status={errors.subject ? 'error' : undefined}
          />
        </div>

        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            Description *
          </Typography.Text>
          <TextArea
            placeholder="Description"
            value={form.description}
            onChange={e => {
              setForm(f => ({ ...f, description: e.target.value }));
              setErrors(e2 => ({ ...e2, description: false }));
            }}
            rows={4}
            status={errors.description ? 'error' : undefined}
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              Priority
            </Typography.Text>
            <Select
              value={form.priority}
              onChange={v => setForm(f => ({ ...f, priority: v }))}
              style={{ width: '100%' }}
              dropdownStyle={{ zIndex: dropdownZIndex }}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              Category *
            </Typography.Text>
            <Select
              placeholder="Select category"
              value={form.category_id || undefined}
              onChange={v => {
                setForm(f => ({ ...f, category_id: v || '' }));
                setErrors(e => ({ ...e, category: false }));
              }}
              allowClear
              style={{ width: '100%' }}
              status={errors.category ? 'error' : undefined}
              dropdownStyle={{ zIndex: dropdownZIndex }}
              options={categories.map((c: any) => ({ value: c.id, label: c.name }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              Type *
            </Typography.Text>
            <Select
              placeholder="Select type"
              value={form.type || undefined}
              onChange={v => {
                setForm(f => ({ ...f, type: v || '' }));
                setErrors(e => ({ ...e, type: false }));
              }}
              style={{ width: '100%' }}
              status={errors.type ? 'error' : undefined}
              dropdownStyle={{ zIndex: dropdownZIndex }}
              options={(ticketTypes || [])
                .filter((t: any) => t.is_active !== false)
                .map((t: any) => ({ value: t.name, label: t.name }))}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTicketFromChatModal;
